import React, { useMemo } from 'react';
import { Box, capitalizeFirstLetter, FeeLabel, Text } from '@common-fe/common-fe';

import { PaymentMethod, PaymentMethodType } from '@/modules/employee/employee.types';
import spacings from '@/styles/spacings';
import theme from '@/styles/theme';
import { getFullAddress } from '@/utils';
import whiteLabelingConfig, { THEMES } from '@/utils/whiteLabeling/whiteLabelingConfig';

import PaymentFreeTimeLabel from '../PaymentFreeTimeLabel';

const isRmrTheme = THEMES.rmr === whiteLabelingConfig.title;

interface Props {
  paymentMethod?: Partial<PaymentMethod>;
  simplifyDesc?: boolean;
  titleFontSize?: string;
  isVerifiedDateVisible?: boolean;
  hasPaymentFreeTimeLabel?: boolean;
  paymentEmployeeFee?: string;
  hasPaymentEmployeeFeeLabel?: boolean;
}

const PaymentMethodDescription: React.FC<Props> = ({
  paymentEmployeeFee,
  hasPaymentFreeTimeLabel = true,
  paymentMethod,
  simplifyDesc,
  titleFontSize,
  isVerifiedDateVisible = true,
  hasPaymentEmployeeFeeLabel,
}) => {
  const { type, accountNumberLast4, accountDescription, isVerified } = paymentMethod || {};
  const currentTitleFontSize = titleFontSize || spacings.m;
  const fee = useMemo(() => hasPaymentEmployeeFeeLabel
    ? (
      <FeeLabel
        amount={paymentEmployeeFee}
        testId={type}
        style={{ marginTop: theme.spacings.spacing2 }}
      />
    ) : null,
  [hasPaymentEmployeeFeeLabel, type, paymentEmployeeFee]);

  if (!paymentMethod) {
    return (
      <Box data-testid={`PaymentMethodLabel_content_${type}`}>
        <Text size={currentTitleFontSize} color="textTitle">
          No payment method added
        </Text>
      </Box>
    );
  }

  switch (type) {
  case PaymentMethodType.PAYPAL:
    return (
      <Box data-testid={`PaymentMethodLabel_content_${type}`}>
        {paymentMethod.requisites?.email && (
          <Text size={currentTitleFontSize} color="textTitle" wordBreak="break-word" data-testId="payment_details_email">
            {paymentMethod.requisites?.email}
          </Text>
        )}
        {paymentMethod.requisites?.phoneNumber && (
          <Text
            size={paymentMethod.requisites?.email ? spacings.s : spacings.m}
            color={paymentMethod.requisites?.email ? 'textSecondary' : 'textTitle'}
            weight={500}
            data-testId="payment_details_phone_number"
          >
            {paymentMethod.requisites?.phoneNumber}
          </Text>
        )}
        {isRmrTheme && hasPaymentFreeTimeLabel ? (
          <Box margin={{ top: 'spacing2' }} width="fit-content">
            <PaymentFreeTimeLabel />
          </Box>
        ) : null}
        {fee}
      </Box>
    );
  case PaymentMethodType.VENMO:
    return paymentMethod.requisites?.phoneNumber && (
      <Box data-testid={`PaymentMethodLabel_content_${type}`}>
        <Text size={currentTitleFontSize} color="textTitle" data-testId="payment_details_phone_number">
          {paymentMethod.requisites?.phoneNumber}
        </Text>
        {isRmrTheme && hasPaymentFreeTimeLabel ? (
          <Box margin={{ top: 'spacing2' }} width="fit-content">
            <PaymentFreeTimeLabel />
          </Box>
        ) : null}
        {fee}
      </Box>
    );
  case PaymentMethodType.DEBIT:
    return (
      <Box data-testid={`PaymentMethodLabel_content_${type}`}>
        <Text size={currentTitleFontSize} color="textTitle" data-testId="payment_details_card_type">
          {capitalizeFirstLetter(paymentMethod.requisites?.cardType || '')} (•••• {paymentMethod.requisites?.cardLast4})
        </Text>
        {isRmrTheme && hasPaymentFreeTimeLabel ? (
          <Box margin={{ top: 'spacing2' }} width="fit-content">
            <PaymentFreeTimeLabel />
          </Box>
        ) : null}
        {fee}
      </Box>
    );
  case PaymentMethodType.DIRECT_DEPOSIT:
    return (
      <Box data-testid={`PaymentMethodLabel_content_${type}`}>
        <Text size={currentTitleFontSize} color="textTitle" data-testId="payment_details_account_description">
          {`${accountDescription} ${simplifyDesc ? '(ends ****)' : `(Bank a/c number ends ${accountNumberLast4 || '****'})`}`}
        </Text>
        {isVerified && isVerifiedDateVisible ? (
          <Box direction="row" gap={spacings.xxxs}>
            <Text size={spacings.s} color="textSecondary" weight={500}>
              Verified on
            </Text>
            <Text size={spacings.s} color="success" weight={500}>
              06/28/21
            </Text>
          </Box>
        ) : null}
        {fee}
      </Box>
    );
  case PaymentMethodType.CHECK:
    return (
      <Box data-testid={`PaymentMethodLabel_content_${type}`}>
        <Text size={currentTitleFontSize} color="textTitle" data-testId="payment_details_address">
          {getFullAddress(paymentMethod?.address)}
        </Text>
        {fee}
      </Box>
    );

  default:
    return null;
  }
};

export default PaymentMethodDescription;
